<template>
  <div class="flex-1 flex flex-col">

    <!-- Add URSB Service Modal -->
    <AppModal v-model="showAddURSBService" title="Add URSB Service" hide-footer>
      <div class="p-4">
        <form @submit.prevent="saveURSBService" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Service Name</label>
            <input type="text" v-model="form.name" placeholder="Service Name" required
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</label>
            <textarea v-model="form.description" placeholder="Service Description" rows="3" required
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"></textarea>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Cover Image</label>
            <input type="file" accept="image/*" required
                   class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
                   @change="onAddCoverChange" />
            <p class="text-xs text-gray-400">Supported formats: PNG, JPG, JPEG. Max size: 5MB</p>
          </div>
          <div class="flex items-center gap-2">
            <input type="checkbox" v-model="form.active" id="active-add"
                   class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
            <label for="active-add" class="text-sm text-gray-600 cursor-pointer">Active Service</label>
          </div>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-save mr-1"></i>
              <span v-if="!loading">Save</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Edit URSB Service Modal -->
    <AppModal v-model="showEditURSBService" title="Edit URSB Service" hide-footer>
      <div class="p-4">
        <form @submit.prevent="updateURSBService" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Service Name</label>
            <input type="text" v-model="editForm.name" placeholder="Service Name" disabled
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</label>
            <textarea v-model="editForm.description" placeholder="Service Description" rows="3" required
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"></textarea>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Cover Image</label>
            <div v-if="editForm.coverImagePath" class="mb-2">
              <img :src="getImageUrl(editForm.coverImagePath)" alt="Current cover"
                   class="max-w-xs max-h-32 rounded border border-gray-200" />
            </div>
            <input type="file" accept="image/*"
                   class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
                   @change="onEditCoverChange" />
            <p class="text-xs text-gray-400">Supported formats: PNG, JPG, JPEG. Max size: 5MB</p>
          </div>
          <p class="text-xs text-gray-400 italic">This action will be logged against your account.</p>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-save mr-1"></i>
              <span v-if="!loading">Save Changes</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Toggle Status Modal -->
    <AppModal v-model="showDeleteURSBService"
              :title="selectedService && selectedService.active ? 'Deactivate URSB Service' : 'Activate URSB Service'"
              hide-footer>
      <div class="p-4 space-y-4">
        <p class="text-sm text-gray-600" v-if="selectedService && selectedService.active">
          Are you sure you want to deactivate this URSB service? It will be unavailable for mobile app users.
        </p>
        <p class="text-sm text-gray-600" v-else>
          Are you sure you want to activate this URSB service? It will become available for mobile app users.
        </p>
        <div class="flex justify-end">
          <button
            type="button"
            @click="toggleURSBServiceStatus"
            :class="selectedService && selectedService.active ? 'btn-sm-danger' : 'btn-sm-primary'"
            :disabled="loading"
          >
            <span v-if="selectedService && selectedService.active">Yes, Deactivate</span>
            <span v-else>Yes, Activate</span>
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Content Editor Modal -->
    <AppModal v-model="showContentEditor" title="Edit Service Content" size="xl" hide-footer>
      <div class="p-4">
        <div class="flex items-center gap-2 mb-4">
          <button type="button" class="btn-sm-primary" :class="{ 'opacity-60': activeTool === 'paragraph' }"
                  @click="addParagraph">
            <i class="fa fa-paragraph mr-1"></i> Paragraph
          </button>
          <button type="button" class="btn-sm-primary" @click="addHeading">
            <i class="fa fa-header mr-1"></i> Heading
          </button>
          <button type="button" class="btn-sm-primary" @click="addBulletList">
            <i class="fa fa-list-ul mr-1"></i> Bullet List
          </button>
          <button type="button" class="btn-sm-primary" @click="addLink">
            <i class="fa fa-link mr-1"></i> Link
          </button>
        </div>

        <div class="space-y-3 max-h-96 overflow-y-auto">
          <div
            v-for="(block, index) in contentDocument.children"
            :key="index"
            class="border border-gray-200 rounded-md p-3 bg-white">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-gray-500 uppercase">{{ getBlockLabel(block.type) }}</span>
              <div class="flex gap-1">
                <button type="button" class="btn-sm-primary" @click="editBlock(index)">
                  <i class="fa fa-edit"></i>
                </button>
                <button type="button" class="btn-sm-danger" @click="deleteBlock(index)">
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>

            <div v-if="!block.editing" class="text-sm text-gray-700">
              <template v-if="block.type === 'paragraph'">
                <p v-for="(child, ci) in block.children" :key="ci">{{ child.content }}</p>
              </template>
              <template v-else-if="block.type === 'heading'">
                <h4 v-for="(child, ci) in block.children" :key="ci" class="font-semibold">{{ child.content }}</h4>
              </template>
              <ul v-else-if="block.type === 'bullet'" class="list-disc ml-4">
                <li v-for="(child, ci) in block.children" :key="ci">{{ child.content }}</li>
              </ul>
              <div v-else-if="block.type === 'link'">
                <span class="text-xs text-gray-500">Text:</span> {{ block.content }}<br />
                <span class="text-xs text-gray-500">URL:</span> {{ block.url }}
              </div>
            </div>

            <div v-if="block.editing" class="space-y-2 mt-2">
              <div v-if="block.type === 'paragraph' || block.type === 'heading'">
                <textarea v-model="block.children[0].content" rows="3"
                          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"></textarea>
              </div>
              <div v-else-if="block.type === 'bullet'" class="space-y-2">
                <div v-for="(child, childIndex) in block.children" :key="childIndex" class="flex gap-2">
                  <input type="text" v-model="child.content"
                         class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
                  <button type="button" class="btn-sm-danger" @click="removeListItem(index, childIndex)">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
                <button type="button" class="btn-sm-primary" @click="addListItem(index)">
                  <i class="fa fa-plus mr-1"></i> Add Item
                </button>
              </div>
              <div v-else-if="block.type === 'link'" class="space-y-2">
                <input type="text" v-model="block.url" placeholder="URL"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
                <input type="text" v-model="block.content" placeholder="Link Text"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
              </div>
              <div class="flex gap-2 pt-1">
                <button type="button" class="btn-sm-primary" @click="saveBlockEdit(index)">
                  <i class="fa fa-check mr-1"></i> Done
                </button>
                <button type="button" class="btn-sm-danger" @click="cancelBlockEdit(index)">
                  <i class="fa fa-times mr-1"></i> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-gray-100 mt-4 pt-3 flex justify-end gap-2">
          <button type="button" class="btn-sm-primary" @click="showContentEditor = false">Cancel</button>
          <button type="button" class="btn-primary" @click="saveContent" :disabled="loading">
            <i class="fa fa-save mr-1"></i>
            <span v-if="!loading">Save Content</span>
            <span v-else>Saving...</span>
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Action Bar -->
    <div class="flex items-center justify-end gap-3 mb-4">
      <select v-model="statusFilter" @change="fetchURSBServices"
              class="px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
        <option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
      </select>
      <button class="btn-primary" @click="openAddForm">
        <i class="fa fa-plus"></i> Add URSB Service
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded border border-gray-200 overflow-x-auto">
      <p v-if="statusFilter === 'active'" class="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
        Use the arrows to set the order services appear in the mobile app.
      </p>
      <div v-if="loading && ursbServices.length === 0" class="py-10 text-center text-sm text-gray-400">
        Loading URSB services...
      </div>
      <table v-else class="min-w-full leading-normal">
        <thead>
        <tr class="border-b border-t border-gray-200">
          <th class="t-header w-12">#</th>
          <th class="t-header w-24 text-center">Order</th>
          <th class="t-header">Name</th>
          <th class="t-header">Description</th>
          <th class="t-header">Status</th>
          <th class="t-header text-center">Actions</th>
        </tr>
        </thead>
        <tbody class="ursb-services-body">
        <tr
          :class="[
            (idx % 2) ? rows.even : rows.odd,
            movingServiceId === service.id ? 'ursb-row-moving' : '',
          ]"
          v-for="(service, idx) in ursbServices"
          :key="service.id"
        >
          <td class="t-body">{{ idx + 1 }}</td>
          <td class="t-body">
            <div class="order-controls">
              <button
                type="button"
                class="order-btn"
                title="Move up"
                :disabled="!!movingServiceId || idx === 0"
                @click="moveService(service, 'UP', idx)"
              >
                <i
                  class="fa-solid"
                  :class="movingServiceId === service.id && movingDirection === 'UP' ? 'fa-spinner fa-spin' : 'fa-chevron-up'"
                ></i>
              </button>
              <button
                type="button"
                class="order-btn"
                title="Move down"
                :disabled="!!movingServiceId || idx === ursbServices.length - 1"
                @click="moveService(service, 'DOWN', idx)"
              >
                <i
                  class="fa-solid"
                  :class="movingServiceId === service.id && movingDirection === 'DOWN' ? 'fa-spinner fa-spin' : 'fa-chevron-down'"
                ></i>
              </button>
            </div>
          </td>
          <td class="t-body font-semibold">{{ service.name }}</td>
          <td class="t-body">{{ service.description }}</td>
          <td class="t-body">
            <span class="text-green-600 font-medium text-xs" v-if="service.active">Active</span>
            <span class="text-red-500 font-medium text-xs" v-else>Inactive</span>
          </td>
          <td class="t-body">
            <div class="flex flex-wrap gap-2 justify-center">
              <button @click="openEditForm(service)" class="btn-sm-primary" title="Edit" :disabled="!!movingServiceId">
                <i class="fa fa-pencil"></i>
              </button>
              <button @click="openContentEditor(service)" class="btn-sm-primary" title="Content" :disabled="!!movingServiceId">
                <i class="fa fa-file-text"></i>
              </button>
              <button @click="openDeleteForm(service)" :class="service.active ? 'btn-sm-danger' : 'btn-sm-primary'"
                      :title="service.active ? 'Deactivate' : 'Activate'"
                      :disabled="!!movingServiceId">
                <i :class="service.active ? 'fa fa-ban' : 'fa fa-check'"></i>
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="ursbServices.length === 0">
          <td colspan="6" class="text-center py-6 text-sm text-gray-400">No URSB services found</td>
        </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import AppModal from "@/components/ui/AppModal.vue";
import { api } from "@/app/services/api.service.ts";
import { useToast } from "@/composables";
import { extractErrorMessage } from "@/domains/home/services/configurations/views/mobile/mobileApiUtils";

export default {
  name: "URSBService",
  components: { AppModal },
  setup() {
    const { successToast, errorToast, warningToast } = useToast();
    return { successToast, errorToast, warningToast };
  },
  data() {
    return {
      showAddURSBService: false,
      showEditURSBService: false,
      showDeleteURSBService: false,
      showContentEditor: false,
      loading: false,
      movingServiceId: null,
      movingDirection: null,
      statusFilter: "active",
      rows: {
        odd: "border-t border-gray-200 hover:bg-gray-10",
        even: "border-t border-gray-200 bg-gray-10"
      },
      statusOptions: [
        { value: "active", text: "Active" },
        { value: "inactive", text: "Inactive" }
      ],
      form: { name: "", description: "", coverImage: null, active: true },
      editForm: { id: null, name: "", description: "", coverImage: null, coverImagePath: null, active: true },
      selectedService: null,
      ursbServices: [],
      contentDocument: { type: "document", version: 1, locale: "en", children: [] },
      activeTool: null,
      currentServiceId: null
    };
  },
  async mounted() {
    await this.fetchURSBServices();
  },
  methods: {
    async fetchURSBServices() {
      this.loading = true;
      try {
        const response = await api().get(`/configuration/ursb-services?isActive=${this.statusFilter === "active"}`);
        this.ursbServices = Array.isArray(response.data)
          ? response.data
          : (response.data?.data ?? []);
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Error fetching URSB services"));
      } finally {
        this.loading = false;
      }
    },
    async moveService(service, direction, index) {
      if (!service?.id || this.movingServiceId) return;
      if (direction === "UP" && index === 0) return;
      if (direction === "DOWN" && index === this.ursbServices.length - 1) return;

      const previousOrder = [...this.ursbServices];
      const swapIndex = direction === "UP" ? index - 1 : index + 1;
      const updated = [...this.ursbServices];
      [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
      this.ursbServices = updated;

      this.movingServiceId = service.id;
      this.movingDirection = direction;

      try {
        const response = await api().post(
          `/configuration/ursb-services/${service.id}/move`,
          null,
          { params: { direction } }
        );
        const message =
          response?.data?.message || "Service order updated successfully";
        this.successToast("Success", message);
        await this.fetchURSBServices();
      } catch (error) {
        this.ursbServices = previousOrder;
        this.errorToast("Error", extractErrorMessage(error, "Could not update service order"));
      } finally {
        this.movingServiceId = null;
        this.movingDirection = null;
      }
    },
    openAddForm() {
      this.resetForm();
      this.showAddURSBService = true;
    },
    openEditForm(service) {
      this.editForm = { ...service };
      this.showEditURSBService = true;
    },
    openDeleteForm(service) {
      this.selectedService = service;
      this.showDeleteURSBService = true;
    },
    openContentEditor(service) {
      this.currentServiceId = service.id;
      this.loadContent(service.id);
      this.showContentEditor = true;
    },
    async loadContent(serviceId) {
      try {
        const response = await api().get(`/configuration/ursb-services/${serviceId}`);
        this.contentDocument = response?.data?.data?.content || {
          type: "document",
          version: 1,
          locale: "en",
          children: []
        };
        if (this.contentDocument.children) {
          this.contentDocument.children.forEach(block => {
            block.editing = false;
          });
        }
      } catch {
        this.contentDocument = { type: "document", version: 1, locale: "en", children: [] };
      }
    },
    resetForm() {
      this.form = { name: "", description: "", coverImage: null, active: true };
    },
    onAddCoverChange(event) {
      const file = event.target.files?.[0];
      this.form.coverImage = file ?? null;
    },
    onEditCoverChange(event) {
      const file = event.target.files?.[0];
      this.editForm.coverImage = file ?? null;
    },
    async saveURSBService() {
      if (!this.form.coverImage) {
        this.warningToast("Validation", "Select a cover image");
        return;
      }
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("name", this.form.name);
        formData.append("description", this.form.description);
        formData.append("active", String(this.form.active));
        formData.append("coverImage", this.form.coverImage);
        await api().post("/configuration/ursb-services", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.successToast("Success", "URSB service added successfully");
        this.showAddURSBService = false;
        this.resetForm();
        await this.fetchURSBServices();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Error adding URSB service"));
      } finally {
        this.loading = false;
      }
    },
    async updateURSBService() {
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("name", this.editForm.name);
        formData.append("description", this.editForm.description);
        formData.append("active", String(this.editForm.active));
        if (this.editForm.coverImage) formData.append("coverImage", this.editForm.coverImage);
        await api().post("/configuration/ursb-services", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.successToast("Success", "URSB service updated successfully");
        this.showEditURSBService = false;
        await this.fetchURSBServices();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Error updating URSB service"));
      } finally {
        this.loading = false;
      }
    },
    async toggleURSBServiceStatus() {
      this.loading = true;
      try {
        await api().post(`/configuration/ursb-services/${this.selectedService.id}/toggle-active`);
        const action = this.selectedService.active ? "deactivated" : "activated";
        this.successToast("Success", `URSB service ${action} successfully`);
        this.showDeleteURSBService = false;
        this.selectedService = null;
        await this.fetchURSBServices();
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Error updating URSB service status"));
      } finally {
        this.loading = false;
      }
    },
    async saveContent() {
      this.loading = true;
      try {
        const contentToSave = {
          ...this.contentDocument,
          children: this.contentDocument.children.map(block => {
            const clean = { ...block };
            delete clean.editing;
            return clean;
          })
        };
        await api().put(`/configuration/ursb-services/${this.currentServiceId}/content`, contentToSave);
        this.successToast("Success", "Content saved successfully");
        this.showContentEditor = false;
      } catch (error) {
        this.errorToast("Error", extractErrorMessage(error, "Error saving content"));
      } finally {
        this.loading = false;
      }
    },
    addParagraph() {
      this.activeTool = "paragraph";
      this.contentDocument.children.push({
        type: "paragraph",
        children: [{ type: "text", content: "New paragraph content..." }],
        editing: true
      });
    },
    addHeading() {
      this.activeTool = "heading";
      this.contentDocument.children.push({
        type: "heading",
        children: [{ type: "text", content: "New heading..." }],
        editing: true
      });
    },
    addBulletList() {
      this.activeTool = "bullet";
      this.contentDocument.children.push({
        type: "bullet",
        children: [{ type: "text", content: "First item" }, { type: "text", content: "Second item" }],
        editing: true
      });
    },
    addLink() {
      this.activeTool = "link";
      this.contentDocument.children.push({ type: "link", url: "", content: "Link text", editing: true });
    },
    editBlock(index) {
      this.contentDocument.children[index].editing = true;
    },
    saveBlockEdit(index) {
      this.contentDocument.children[index].editing = false;
    },
    cancelBlockEdit(index) {
      this.contentDocument.children[index].editing = false;
    },
    deleteBlock(index) {
      this.contentDocument.children.splice(index, 1);
    },
    removeListItem(blockIndex, itemIndex) {
      this.contentDocument.children[blockIndex].children.splice(itemIndex, 1);
    },
    addListItem(blockIndex) {
      this.contentDocument.children[blockIndex].children.push({ type: "text", content: "New item" });
    },
    getBlockLabel(type) {
      return { paragraph: "Paragraph", heading: "Heading", bullet: "Bullet List", link: "Link" }[type] || type;
    },
    getImageUrl(imagePath) {
      if (!imagePath) return "";
      return `${import.meta.env.VITE_API_BASE_URL}/configuration/files?attachment=${imagePath}`;
    }
  }
};
</script>

<style scoped>
.order-controls {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
}

.order-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  color: #4b5563;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.order-btn:hover:not(:disabled) {
  border-color: var(--color-primary, #2563eb);
  color: var(--color-primary, #2563eb);
  background: #eff6ff;
}

.order-btn:active:not(:disabled) {
  transform: scale(0.94);
}

.order-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ursb-services-body tr {
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.ursb-row-moving {
  background-color: #eff6ff !important;
}
</style>
